import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import type {
  FormResponseListBlock as FormResponseListBlockType,
  PinnedFormsListBlock as PinnedFormsListBlockType,
  FileListBlock as FileListBlockType,
} from "../../../shared/schema";
import { FormResponseListBlock } from "../../components/blocks/FormResponseListBlock";
import { PinnedFormsListBlock } from "../../components/blocks/PinnedFormsListBlock";
import { FileListBlock } from "../../components/blocks/FileListBlock";
import { HeadingBlock } from "../../components/blocks/HeadingBlock";

interface LegacyDocumentsPageProps {
  category?: "submitted" | "outstanding" | "files" | "all";
}

// Default block configurations for legacy documents page
const createFormResponseListBlock = (
  categories: ("submitted" | "unsubmitted" | "all")[]
): FormResponseListBlockType => ({
  id: "legacy-form-response-list",
  type: "form-response-list",
  props: {
    maxItems: 10,
    categories,
  },
});

const createPinnedFormsListBlock = (): PinnedFormsListBlockType => ({
  id: "legacy-pinned-forms-list",
  type: "pinned-forms-list",
  props: {
    maxItems: 10,
  },
});

const createFileListBlock = (): FileListBlockType => ({
  id: "legacy-file-list",
  type: "file-list",
  props: {
    maxItems: 20,
  },
});

/**
 * Legacy documents page that mirrors the old portal's /documents route.
 * Renders appropriate blocks based on the category prop:
 * - "all": Shows pinned forms, submitted forms, and files
 * - "submitted": Shows only submitted form responses
 * - "outstanding": Shows only pinned forms (forms to complete)
 * - "files": Shows only files
 */
export const LegacyDocumentsPage = ({ category = "all" }: LegacyDocumentsPageProps) => {
  const { t } = useTranslation("forms");
  const [searchParams] = useSearchParams();

  // Form group filtering from URL params (used by {{form_groups.ID.link}} template)
  const formGroupId = searchParams.get("groupId") ?? undefined;
  const formGroupInstance = searchParams.get("groupInstance") ?? undefined;

  const showPinnedForms = category === "all" || category === "outstanding";
  const showSubmittedForms = category === "all" || category === "submitted";
  const showFiles = category === "all" || category === "files";

  const getPageTitle = () => {
    switch (category) {
      case "submitted":
        return t("Submitted Forms");
      case "outstanding":
        return t("Outstanding Forms");
      case "files":
        return t("Files");
      default:
        return t("Documents");
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      <div className="mb-6">
        <HeadingBlock text={getPageTitle()} level="h1" />
      </div>

      <div className="space-y-8">
        {showPinnedForms && (
          <section>
            {category === "all" && (
              <div className="mb-3">
                <HeadingBlock text={t("Forms to Complete")} level="h2" />
              </div>
            )}
            <PinnedFormsListBlock
              block={createPinnedFormsListBlock()}
              formGroupId={formGroupId}
              formGroupInstance={formGroupInstance}
            />
          </section>
        )}

        {showSubmittedForms && (
          <section>
            {category === "all" && (
              <div className="mb-3">
                <HeadingBlock text={t("Submitted Forms")} level="h2" />
              </div>
            )}
            <FormResponseListBlock block={createFormResponseListBlock(["submitted"])} />
          </section>
        )}

        {showFiles && (
          <section>
            {category === "all" && (
              <div className="mb-3">
                <HeadingBlock text={t("Files")} level="h2" />
              </div>
            )}
            <FileListBlock block={createFileListBlock()} />
          </section>
        )}
      </div>
    </div>
  );
};
